import * as React from 'react';
import {
  IconCheckmarkCircle,
  IconHelp,
  IconInfo,
  IconPlaceholder,
  IconWarning,
  IconWarningAlt,
  IconXCircle,
} from '../icons';
import useStyles from './use_styles';
import { InlineNotificationInterface } from './types';
import { useTheme } from '@veneer/theme';
import highContrastMediaQuery from '../utils/high_contrast_media_query';
import Button from '../button';
import UtilityButton from '../shared/utility_button';
import notificationI18n from './i18n';

const inlineIcon = {
  caution: IconWarning,
  custom: IconPlaceholder,
  help: IconHelp,
  informative: IconInfo,
  negative: IconXCircle,
  positive: IconCheckmarkCircle,
  warning: IconWarningAlt,
};

const InlineNotification = React.forwardRef<
  HTMLDivElement,
  InlineNotificationInterface &
    Omit<React.HTMLAttributes<HTMLElement>, keyof InlineNotificationInterface>
>(
  (
    {
      action,
      actionPlacement = 'bottom-start',
      customStyle,
      date,
      description,
      hideIcon = false,
      i18n,
      icon,
      iconAction,
      id,
      onClose,
      closeButton = true,
      title,
      type = 'informative',
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const isHc = theme.mode === 'contrast';
    const isPill = theme.shape === 'pill';
    const style = useStyles();

    const handleActionClick = React.useCallback(() => {
      action?.action(id);
    }, [action, id]);

    const handleCloseClick = React.useCallback(() => {
      onClose?.(id);
    }, [id, onClose]);

    const handleIconActionClick = React.useCallback(() => {
      iconAction?.action?.(id);
    }, [id, iconAction]);

    const DefaultIcon = inlineIcon[type];
    const themedIcon = !icon ? (
      <DefaultIcon
        customStyle={[style.icon.base, type && style.icon.type[type]]}
        filled
      />
    ) : (
      icon
    );
    const hasSingleContent =
      [!!title, !!description, !!date].filter(Boolean).length < 2;

    return (
      <div
        {...rest}
        ref={ref}
        css={[
          style.root.base,
          style.root[type],
          isPill && style.root.pill,
          isHc && style.root.hc,
          highContrastMediaQuery(theme),
          customStyle,
          actionPlacement != 'top' && style.root.actionBottom,
        ]}
      >
        <div
          css={[
            style.contentContainer.base,
            (!action || actionPlacement == 'top') &&
              hasSingleContent &&
              style.contentContainer.singleContent,
          ]}
        >
          {!hideIcon && themedIcon}
          <div
            css={[
              style.centerContainer.base,
              actionPlacement == 'top' &&
                style.centerContainer.actionTopPlacement,
            ]}
          >
            {(title || description || date) && (
              <div css={[style.textContainer.base]}>
                {title && <div css={style.title}>{title}</div>}
                {description && (
                  <div css={style.description}>{description}</div>
                )}
                {date && <div css={style.date}>{date}</div>}
              </div>
            )}
            {action && (
              <div
                css={[
                  style.action.base,
                  actionPlacement == 'top' && style.action.top,
                ]}
              >
                <Button
                  onClick={handleActionClick}
                  appearance={actionPlacement == 'top' ? 'ghost' : 'secondary'}
                  small
                  customStyle={style.action.ellipsis}
                >
                  {action.label}
                </Button>
              </div>
            )}
          </div>
        </div>
        {(iconAction || closeButton) && (
          <div css={[style.buttonsContainer.base]}>
            {iconAction && (
              <UtilityButton
                aria-label={iconAction['aria-label']}
                icon={iconAction.icon}
                onClick={handleIconActionClick}
              />
            )}
            {closeButton && (
              <UtilityButton
                aria-label={i18n?.close || notificationI18n.close}
                onClick={handleCloseClick}
              />
            )}
          </div>
        )}
      </div>
    );
  }
);

InlineNotification.displayName = 'InlineNotification';

export default InlineNotification;
